import { useCallback, useRef } from 'react';

export const useFocusTrap = () => {
    const cleanupRef = useRef<(() => void) | null>(null);

    const trapFocus = useCallback((element: HTMLElement | null, enabled?: boolean) => {
        // Cleanup previous focus trap if exists
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }

        if (!element || !enabled) return () => {};

        const focusableElements = Array.from(
            element.querySelectorAll('a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])')
        ).filter((el) => {
            const htmlEl = el as HTMLElement;
            return !htmlEl.hasAttribute('disabled') && !!(htmlEl.offsetWidth || htmlEl.offsetHeight || htmlEl.getClientRects().length);
        }) as HTMLElement[];

        if (focusableElements.length < 2) return () => {};

        // Function to get modal region elements sorted by order
        const getModalRegions = (): HTMLElement[] => {
            const regions = element.querySelectorAll('[data-modal-region="true"]');
            const visibleRegions = Array.from(regions).filter((el) => {
                const htmlEl = el as HTMLElement;
                return !!(htmlEl.offsetWidth || htmlEl.offsetHeight || htmlEl.getClientRects().length);
            }) as HTMLElement[];

            // Sort by data-modal-region-order if available, otherwise by DOM order
            return visibleRegions.sort((a, b) => {
                const orderA = parseInt(a.getAttribute('data-modal-region-order') || '0', 10);
                const orderB = parseInt(b.getAttribute('data-modal-region-order') || '0', 10);

                if (orderA !== orderB) {
                    return orderA - orderB;
                }

                // Fallback to DOM order if no order attribute or same order
                const position = a.compareDocumentPosition(b);
                return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
            });
        };

        // Function to get current region context
        const getCurrentRegionContext = (
            focusedElement: HTMLElement
        ): {
            isInRegion: boolean;
            regionIndex: number;
            availableRegions: HTMLElement[];
            hasModalRegionNav: boolean;
        } => {
            const availableRegions = getModalRegions();
            let isInRegion = false;
            let regionIndex = -1;
            const hasModalRegionNav = focusedElement.getAttribute('data-modal-region') === 'true';

            // Check if focused element is a region or inside a region
            for (let i = 0; i < availableRegions.length; i++) {
                if (availableRegions[i] === focusedElement || availableRegions[i].contains(focusedElement)) {
                    isInRegion = true;
                    regionIndex = i;
                    break;
                }
            }

            return { isInRegion, regionIndex, availableRegions, hasModalRegionNav };
        };

        // Function to find nearest region before or after current element
        const findNearestRegion = (currentElement: HTMLElement, availableRegions: HTMLElement[], direction: 'up' | 'down'): HTMLElement | null => {
            if (availableRegions.length === 0) return null;

            // Get all visible elements in the container
            const allElements = Array.from(element.querySelectorAll('*')).filter((el) => {
                const htmlEl = el as HTMLElement;
                return !!(htmlEl.offsetWidth || htmlEl.offsetHeight || htmlEl.getClientRects().length);
            }) as HTMLElement[];

            const currentIndex = allElements.indexOf(currentElement);
            if (currentIndex === -1) return null;

            // Map regions to their positions in DOM
            const sortedRegions = availableRegions
                .map((region) => ({
                    element: region,
                    index: allElements.indexOf(region),
                }))
                .filter((item) => item.index !== -1)
                .sort((a, b) => a.index - b.index);

            if (direction === 'down') {
                // Find first region after current element
                const nextRegion = sortedRegions.find((region) => region.index > currentIndex);
                // If no region after, wrap to first region
                return nextRegion ? nextRegion.element : sortedRegions.length > 0 ? sortedRegions[0].element : null;
            } else {
                // Find last region before current element
                const prevRegions = sortedRegions.filter((region) => region.index < currentIndex);
                // If no region before, wrap to last region
                if (prevRegions.length > 0 && prevRegions[prevRegions.length - 2]) {
                    return prevRegions[prevRegions.length - 2].element;
                } else {
                    return sortedRegions.length > 0 ? sortedRegions[sortedRegions.length - 1].element : null;
                }
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

            // Handle ArrowUp and ArrowDown for modal region navigation
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                const focusedElement = document.activeElement as HTMLElement;
                const context = getCurrentRegionContext(focusedElement);

                // Only handle if there are regions available
                if (context.availableRegions.length > 0) {
                    e.preventDefault();
                    e.stopPropagation();

                    if (e.key === 'ArrowUp') {
                        if (context.hasModalRegionNav && context.isInRegion) {
                            // Currently in a region, move to previous region
                            const prevIndex = context.regionIndex - 1;
                            if (prevIndex >= 0) {
                                context.availableRegions[prevIndex].focus();
                            } else {
                                // At first region, wrap to last region
                                context.availableRegions[context.availableRegions.length - 1].focus();
                            }
                        } else {
                            // Not in a region, find nearest region before current element (with wrap)
                            const nearestRegion = findNearestRegion(focusedElement, context.availableRegions, 'up');
                            if (nearestRegion) {
                                nearestRegion.focus();
                            }
                        }
                    } else {
                        // ArrowDown
                        if (context.hasModalRegionNav && context.isInRegion) {
                            // Currently in a region, move to next region
                            const nextIndex = context.regionIndex + 1;
                            if (nextIndex < context.availableRegions.length) {
                                context.availableRegions[nextIndex].focus();
                            } else {
                                // At last region, wrap to first region
                                context.availableRegions[0].focus();
                            }
                        } else {
                            // Not in a region, find nearest region after current element (with wrap)
                            const nearestRegion = findNearestRegion(focusedElement, context.availableRegions, 'down');
                            if (nearestRegion) {
                                nearestRegion.focus();
                            }
                        }
                    }
                    return;
                }
            }

            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                e.stopPropagation();
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
                focusableElements[prevIndex].focus();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                e.stopPropagation();
                const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
                focusableElements[nextIndex].focus();
            } else if (e.key === 'Tab' && e.shiftKey) {
                e.preventDefault();
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
                focusableElements[prevIndex].focus();
            } else if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
                focusableElements[nextIndex].focus();
            }
        };

        // Add event listener to the container to catch all keydown events
        element.addEventListener('keydown', handleKeyDown);

        const cleanup = () => {
            element.removeEventListener('keydown', handleKeyDown);
        };

        cleanupRef.current = cleanup;

        return cleanup;
    }, []);

    const releaseFocus = useCallback(() => {
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
    }, []);

    return {
        trapFocus,
        releaseFocus,
    };
};
